import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { FaTimes } from "react-icons/fa";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useForm } from "react-hook-form";
import { IFinancePurchaseOrderListResponseData } from "@/api_framework/api_modals/Inventory";

export interface ISendToFinanceProps {
  reason: string;
}

interface ISapModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sapdata: IFinancePurchaseOrderListResponseData | null;
  onApprove: (accountId: string) => void;
  loading: boolean;
  selectedBankAccountId: string;
  onSelectBankAccount: (accountId: string) => void;
}

export default function SapModal({
  open,
  setOpen,
  sapdata,
  onApprove,
  loading,
  selectedBankAccountId,
  onSelectBankAccount,
}: ISapModalProps) {
  const cancelButtonRef = useRef(null);
  const formHook = useForm<{ payment_account_id: string }>({
    mode: "onChange",
    defaultValues: {
      payment_account_id: "",
    },
  });
  const bankingAccounts = useMemo(
    () =>
      [
        ...(sapdata?.sap_data?.banking_details || []).map((account) => ({
          id: account.account_id,
          title: account.account_name,
        })),
        { id: "null", title: "None" },
      ],
    [sapdata],
  );

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    formHook.setValue("payment_account_id", selectedBankAccountId || "");
  }, [selectedBankAccountId]);

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40"
          initialFocus={cancelButtonRef}
          onClose={() => {
            if (!loading) {
              handleClose();
            }
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={`relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-4 sm:w-[480px] sm:p-6 flex flex-col `}
                >
                  {loading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-50 rounded-lg">
                      <div className="h-10 w-10 border-4 border-black border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  <button
                    onClick={handleClose}
                    disabled={loading}
                    className={`absolute top-6 right-3 ${
                      loading ? "text-gray-300 cursor-not-allowed" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <FaTimes size={16} />
                  </button>
                  <div className="pb-2">
                    <div className="font-semibold text-sm">Please Check the Details:</div>
                  </div>
                  {sapdata?.sap_data && (
                    <div className="grid grid-cols-[200px_1fr] gap-y-3 text-sm mt-3">
                      <div className="font-medium text-gray-600">Clinic Name</div>
                      <div>{sapdata.sap_data.clinic_name}</div>
                      <div className="font-medium text-gray-600">SAP Warehouse Code</div>
                      <div>{sapdata.sap_data.warehouse_code}</div>
                      <div className="font-medium text-gray-600">SAP Vendor Name</div>
                      <div>{sapdata.sap_data.vendor_name}</div>
                      <div className="font-medium text-gray-600">SAP Vendor Code</div>
                      <div>{sapdata.sap_data.vendor_code}</div>
                      <div className="font-medium text-gray-600">Cost (Without Tax)</div>
                      <div>{sapdata.sap_data.total_cost_before_tax}</div>
                      <div className="font-medium text-gray-600">Tax Value</div>
                      <div>{sapdata.sap_data.total_tax}</div>
                      <div className="font-medium text-gray-600">Total Cost</div>
                      <div>{sapdata.sap_data.total_cost_after_tax}</div>
                      {sapdata?.sap_data?.item_sub_category?.map((item: any, index: number) => (
                        <Fragment key={`sub-${index}`}>
                          <div className="font-medium text-gray-600 text-xs">{item?.sub_category}</div>
                          <div className="text-xs">{item?.amount}</div>
                        </Fragment>
                      ))}
                      {sapdata?.sap_data?.tax_details?.map((tax: any, index: number) => (
                        <Fragment key={`tax-${index}`}>
                          <div className="font-medium text-gray-600 text-xs">{tax?.[0]}</div>
                          <div className="text-xs">{tax?.[1]}</div>
                        </Fragment>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 grid grid-cols-[200px_1fr] gap-y-3 text-sm">
                    <div className="font-medium text-gray-600 text-sm">Payment via</div>
                    <NonSearchableSelectMenu
                      errorMessages={[]}
                      onSelectItem={(item) => {
                        const accountId = item?.id || "";
                        formHook.setValue("payment_account_id", accountId);
                        onSelectBankAccount(accountId);
                      }}
                      fieldError={formHook.formState.errors.payment_account_id}
                      register={formHook.register("payment_account_id")}
                      selectItems={bankingAccounts}
                      placeholder="Select payment account"
                      showTooltips={false}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={bankingAccounts.find((item) => item.id === selectedBankAccountId) || null}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full max-w-[260px]"
                    />
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md"
                      onClick={handleClose}
                      disabled={loading}
                    >
                      Go Back
                    </button>
                    <button
                      className={`px-5 py-2 text-sm rounded-md flex items-center justify-center gap-2
    ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-black text-white"}
  `}
                      onClick={() => onApprove(selectedBankAccountId)}
                      disabled={loading}
                    >
                      {loading && (
                        <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      )}
                      {loading ? "Approving..." : "Yes, Approve"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
